import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../lib/apiError';
import { toast } from 'sonner';
import { 
  Search, 
  MapPin, 
  Star, 
  Truck,
  Pill,
  ShoppingCart,
  Utensils,
  Zap,
  Car,
  Hospital,
  Filter,
  CheckCircle2,
  Plus,
  TrendingUp,
  Package
} from 'lucide-react';

const CATEGORY_ICONS = {
  'Pharmacy': Pill,
  'Grocery': ShoppingCart,
  'Restaurant': Utensils,
  'Utility': Zap,
  'Transport': Car,
  'Hospital': Hospital
};

const CATEGORY_COLORS = {
  'Pharmacy': 'bg-green-100 text-green-700',
  'Grocery': 'bg-blue-100 text-blue-700',
  'Restaurant': 'bg-orange-100 text-orange-700',
  'Utility': 'bg-yellow-100 text-yellow-700',
  'Transport': 'bg-burgundy-100 text-burgundy-700',
  'Hospital': 'bg-red-100 text-red-700'
};

const PartnersPage = () => {
  const { vendors, fetchData } = useApp();
  const { authApi } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({
    name: '',
    category: 'Grocery',
    city: '',
    state: '',
    address: '',
    phone: '',
    operating_hours: '',
    delivery_available: false,
    delivery_radius: '',
  });

  const categories = ['All', 'Pharmacy', 'Grocery', 'Restaurant', 'Utility', 'Transport', 'Hospital'];

  const filteredVendors = vendors.filter(vendor => {
    const name = String(vendor.name || '').toLowerCase();
    const city = String(vendor.city || '').toLowerCase();
    const matchesSearch = name.includes(searchQuery.toLowerCase()) ||
                          city.includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || vendor.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalOrders = vendors.reduce((sum, v) => sum + Number(v.totalOrders ?? v.total_orders ?? 0), 0);
  const avgRating = vendors.length > 0 
    ? (vendors.reduce((sum, v) => sum + Number(v.rating || 0), 0) / vendors.length).toFixed(1) 
    : 0;

  const handleCreateVendor = async (event) => {
    event.preventDefault();
    setSavingVendor(true);
    try {
      const payload = {
        ...newVendor,
        delivery_radius: newVendor.delivery_available && newVendor.delivery_radius
          ? Number(newVendor.delivery_radius)
          : null,
      };
      await authApi.post('/vendors', payload);
      toast.success('Vendor added');
      setShowVendorForm(false);
      setNewVendor({
        name: '',
        category: 'Grocery',
        city: '',
        state: '',
        address: '',
        phone: '',
        operating_hours: '',
        delivery_available: false,
        delivery_radius: '',
      });
      await fetchData({ force: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to add vendor'));
    } finally {
      setSavingVendor(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="partners-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-gray-900">Partner Vendors</h1>
              <p className="text-gray-500 mt-1">Manage the Pebbles vendor network</p>
            </div>
            <button
              type="button"
              onClick={() => setShowVendorForm(true)}
              className="px-4 py-2 bg-burgundy-600 text-[#FAFAF7] text-sm font-medium rounded-full hover:bg-burgundy-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Vendor
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-5 h-5 text-burgundy-600" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{vendors.length}</p>
            <p className="text-sm text-gray-500">Total Vendors</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-serif font-bold text-green-600">
              {vendors.filter(v => v.verified).length}
            </p>
            <p className="text-sm text-gray-500">Verified</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-serif font-bold text-blue-600">{totalOrders.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Total Orders</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{avgRating}</p>
            <p className="text-sm text-gray-500">Avg Rating</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Truck className="w-5 h-5 text-burgundy-600" />
            </div>
            <p className="text-3xl font-serif font-bold text-burgundy-600">
              {vendors.filter(v => v.deliveryAvailable || v.delivery_available).length}
            </p>
            <p className="text-sm text-gray-500">With Delivery</p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-burgundy-500"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Vendors Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVendors.map((vendor) => {
                  const CategoryIcon = CATEGORY_ICONS[vendor.category] || Package;
                  const deliveryAvailable = vendor.deliveryAvailable ?? vendor.delivery_available;
                  const deliveryRadius = vendor.deliveryRadius ?? vendor.delivery_radius;
                  const totalOrderCount = Number(vendor.totalOrders ?? vendor.total_orders ?? 0);
                  return (
                    <tr key={vendor.id || vendor._id || vendor.name} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${CATEGORY_COLORS[vendor.category]?.split(' ')[0] || 'bg-gray-100'} flex items-center justify-center`}>
                            <CategoryIcon className={`w-5 h-5 ${CATEGORY_COLORS[vendor.category]?.split(' ')[1] || 'text-gray-500'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{vendor.name || 'Unnamed vendor'}</p>
                            <p className="text-xs text-gray-500">{vendor.operatingHours || vendor.operating_hours || 'Hours not set'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${CATEGORY_COLORS[vendor.category] || 'bg-gray-100 text-gray-700'}`}>
                          {vendor.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {[vendor.city, vendor.state].filter(Boolean).join(', ') || 'Location not set'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="font-medium text-gray-900">{Number(vendor.rating || 0).toFixed(1)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {totalOrderCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {deliveryAvailable ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <Truck className="w-4 h-4" />
                            {deliveryRadius || '-'}km
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {vendor.verified ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            Verified
                          </span>
                        ) : (
                          <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                            Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showVendorForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <div className="mb-5">
              <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Add Partner Vendor</h2>
              <p className="mt-1 text-sm text-[#5A5A5A]">Create a marketplace vendor record for care expense fulfilment.</p>
            </div>
            <form onSubmit={handleCreateVendor} className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium text-[#1A1A1A]">
                Vendor name
                <input required value={newVendor.name} onChange={(event) => setNewVendor(prev => ({ ...prev, name: event.target.value }))} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm" />
              </label>
              <label className="text-sm font-medium text-[#1A1A1A]">
                Category
                <select value={newVendor.category} onChange={(event) => setNewVendor(prev => ({ ...prev, category: event.target.value }))} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm">
                  {categories.filter(item => item !== 'All').map(category => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-[#1A1A1A]">
                City
                <input required value={newVendor.city} onChange={(event) => setNewVendor(prev => ({ ...prev, city: event.target.value }))} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm" />
              </label>
              <label className="text-sm font-medium text-[#1A1A1A]">
                State
                <input required value={newVendor.state} onChange={(event) => setNewVendor(prev => ({ ...prev, state: event.target.value }))} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm" />
              </label>
              <label className="text-sm font-medium text-[#1A1A1A]">
                Phone
                <input value={newVendor.phone} onChange={(event) => setNewVendor(prev => ({ ...prev, phone: event.target.value }))} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm" />
              </label>
              <label className="text-sm font-medium text-[#1A1A1A]">
                Operating hours
                <input value={newVendor.operating_hours} onChange={(event) => setNewVendor(prev => ({ ...prev, operating_hours: event.target.value }))} placeholder="8am - 8pm" className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm" />
              </label>
              <label className="md:col-span-2 text-sm font-medium text-[#1A1A1A]">
                Address
                <input value={newVendor.address} onChange={(event) => setNewVendor(prev => ({ ...prev, address: event.target.value }))} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm" />
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-[#E8E6E3] bg-white px-3 py-3 text-sm font-medium text-[#1A1A1A]">
                <input type="checkbox" checked={newVendor.delivery_available} onChange={(event) => setNewVendor(prev => ({ ...prev, delivery_available: event.target.checked }))} />
                Delivery available
              </label>
              <label className="text-sm font-medium text-[#1A1A1A]">
                Delivery radius (km)
                <input type="number" min="0" value={newVendor.delivery_radius} onChange={(event) => setNewVendor(prev => ({ ...prev, delivery_radius: event.target.value }))} disabled={!newVendor.delivery_available} className="mt-2 h-11 w-full rounded-xl border border-[#D8D5D0] bg-white px-3 text-sm disabled:opacity-50" />
              </label>
              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowVendorForm(false)} className="rounded-full border border-[#9F1D35] px-5 py-3 text-sm font-medium text-[#9F1D35]">Cancel</button>
                <button type="submit" disabled={savingVendor} className="rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-50">
                  {savingVendor ? 'Saving...' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersPage;
